"use client";

import { ReactNode } from "react";
import Image from "next/image";
import { Link } from "@/i18n/navigation";

import { Badge } from "@/components/ui/badge";
import { Countdown } from "./countdown";

interface SubjectIcon {
	icon: string;
	name: string;
}

interface BannerProps {
	children?: ReactNode;
	className?: string;
	variant?: 'default' | 'with-svg' | 'article' | 'article-compact' | 'article-compact-centered' | 'article-compact-split' | 'college' | 'announcement';
	eyebrowLabel?: string;
	announcementType?: 'surf' | 'aoc' | 'qsre' | 'webinar' | 'jisc';
	webinarDate?: Date | string | number;
	// Article variant props
	title?: string;
	description?: string;
	authorName?: string;
	authorTitle?: string;
	authorImage?: string;
	publishDate?: string;
	readingTime?: string;
	articleImage?: string;
	articleImageAlt?: string;
	articleImageFit?: 'cover' | 'contain';
	// College variant props
	collegeName?: string;
	collegeTitle?: string;
	collegeSubtitle?: string;
	collegeImage?: string;
	collegeImageAlt?: string;
	subjectIcons?: SubjectIcon[];
}

export function Banner({
	children,
	className = "",
	variant = 'default',
	eyebrowLabel,
	announcementType = 'surf',
	webinarDate,
	title,
	description,
	authorName,
	authorTitle,
	authorImage,
	publishDate,
	readingTime,
	articleImage,
	articleImageAlt,
	articleImageFit = 'cover',
	collegeName,
	collegeTitle,
	collegeSubtitle,
	collegeImage,
	collegeImageAlt,
	subjectIcons
}: BannerProps) {
	// Announcement variant
	if (variant === 'announcement') {
		// JISC announcement with countdown
		if (announcementType === 'jisc' && webinarDate) {
			// Convert webinarDate to Date object if it's a string or number
			const targetDate = typeof webinarDate === 'string' || typeof webinarDate === 'number'
				? new Date(webinarDate)
				: webinarDate;

			// Hide banner if the event has passed
			if (targetDate.getTime() <= new Date().getTime()) {
				return null;
			}

			return (
				<section
					className={`relative w-full min-h-10 sm:min-h-10 py-2 md:py-1 md:h-8 overflow-hidden bg-[#ed6b08] ${className}`}
				>
					<div className="relative z-10 h-full">
						<div className="flex flex-row items-center justify-between h-full container mx-auto gap-x-4 px-4 md:px-6">
							<div className="flex items-center gap-x-3 shrink min-w-0">
								<img
									src="/jisc.png"
									alt="JISC"
									className="h-6 w-6 object-contain"
								/>
								<span className="text-white text-base font-semibold">
									<span className="hidden lg:inline">StudyStash is attending Jisc Digifest 2026 in Birmingham</span>
									<span className="lg:hidden">Jisc Digifest 2026 • Birmingham</span>
								</span>
							</div>
							<div className="flex items-center gap-x-5 shrink-0 px-0 py-1 md:py-0">
								{/* <div className="hidden xl:block">
									<Countdown targetDate={targetDate} />
								</div> */}
								<div className="flex items-center shrink-0">
									<Link href="/about/events-webinars/digifest" className="text-white text-base hover:underline font-semibold whitespace-nowrap">
										Read more →
									</Link>
								</div>
							</div>
						</div>
					</div>
				</section>
			);
		}

		// Webinar announcement with countdown
		if (announcementType === 'webinar' && webinarDate) {
			// Convert webinarDate to Date object if it's a string or number
			const targetDate = typeof webinarDate === 'string' || typeof webinarDate === 'number'
				? new Date(webinarDate)
				: webinarDate;

			// Hide banner if the event has passed
			if (targetDate.getTime() <= new Date().getTime()) {
				return null;
			}

			return (
				<section
					className={`relative w-full min-h-10 sm:min-h-10 py-2 md:py-1 md:h-8 overflow-hidden bg-linear-to-r from-ss-blue via-ss-green to-ss-blue ${className}`}
				>
					<div className="relative z-10 h-full">
						<div className="flex flex-row items-center justify-between h-full container mx-auto gap-x-4 px-4 md:px-6">
							<div className="flex items-center gap-x-3 shrink min-w-0">
								<span className="text-white text-base font-semibold">
									<span className="hidden lg:inline">Piloting StudyStash in Higher Education: Lessons Across Four EU Institutions • 29th Jan, 3:30 GMT</span>
									<span className="lg:hidden">StudyStash Webinar: 29th Jan, 3:30 GMT</span>
								</span>
							</div>
							<div className="flex items-center gap-x-5 shrink-0 px-0 py-1 md:py-0">
								<div className="hidden xl:block">
									<Countdown targetDate={targetDate} />
								</div>
								<div className="flex items-center shrink-0">
									<a href="https://webinar.zoho.eu/meeting/register?sessionId=1294108215" target="_blank" rel="noopener noreferrer" className="text-white text-base hover:underline font-semibold whitespace-nowrap">
										Register →
									</a>
								</div>
							</div>
						</div>
					</div>
				</section>
			);
		}

		// QS Reimagine Education Awards announcement
		if (announcementType === 'qsre') {
			return (
				<section
					className={`relative min-h-10 sm:min-h-10 py-2 md:py-1 md:h-8 overflow-hidden ${className}`}
					style={{
						backgroundImage: 'linear-gradient(to right, #4A148C 0%, #6A1B9A 15%, #9C27B0 30%, #AB47BC 40%, #E91E63 50%, #EC407A 60%, #FF6F00 75%, #FF8F00 85%, #FFA726 100%)'
					}}
				>
					<div className="relative z-10 h-full">
						<div className="flex flex-col md:flex-row items-center justify-between h-full container mx-auto gap-y-2 md:gap-y-0 px-4 md:px-6">
							<div className="flex items-center gap-x-3 w-full md:w-auto">
								<img
									src="/qsre.png"
									alt="QS Reimagine Education Awards"
									className="h-6 w-auto object-contain"
								/>
								<span className="text-white text-base font-semibold">
									<span className="hidden sm:inline">QS Reimagine Education Awards; StudyStash has been shortlisted for the AI in Education Award!</span>
									<span className="sm:hidden">QS Reimagine; StudyStash shortlisted!</span>
								</span>
							</div>
							<div className="flex items-center gap-x-3 flex-wrap w-full md:w-auto justify-start md:justify-center px-0 py-1 md:py-0">
								<a href="https://www.reimagine-education.com/" target="_blank" rel="noopener noreferrer" className="hidden sm:block text-white text-base hover:underline font-semibold">
									Read more →
								</a>
								<div className="flex items-center">
									<Link href="/pilot-program" className="text-white text-base hover:underline font-semibold">
										Our offer →
									</Link>
								</div>
							</div>
						</div>
					</div>
				</section>
			);
		}

		// Association of Colleges (AOC) announcement
		if (announcementType === 'aoc') {
			return (
				<section
					className={`relative min-h-8 sm:min-h-8 py-2 md:py-0 md:h-6 overflow-hidden ${className}`}
					style={{
						backgroundColor: '#373B3A'
					}}
				>
					<div className="relative z-10 h-full">
						<div className="flex flex-col md:flex-row items-center justify-between h-full container mx-auto gap-y-2 md:gap-y-0 px-4 md:px-6">
							<div className="flex items-center gap-x-3 w-full md:w-auto">
								<img
									src="/aoc.png"
									alt="Association of Colleges"
									className="h-4 w-auto object-contain"
								/>
								<span className="text-white text-base font-semibold">
									<span className="hidden sm:inline">StudyStash is attending the Association of Colleges - ICC, Birmingham • 18 Nov 2025</span>
									<span className="sm:hidden">AOC - 18 Nov</span>
								</span>
							</div>
							<div className="flex items-center gap-x-3 flex-wrap w-full md:w-auto justify-start md:justify-center px-0">
								<Link href="/about/events-webinars/annual-association-of-colleges" className="hidden sm:block text-white text-base hover:underline font-semibold">
									Read more →
								</Link>
								<div className="flex items-center">
									<Link href="/pilot-program-fe-colleges" className="text-white text-base hover:underline font-semibold">
										Our offer →
									</Link>
								</div>
							</div>
						</div>
					</div>
				</section>
			);
		}

		// Default announcement (ProApptive/Surf)
		return (
			<section
				className={`relative min-h-8 sm:min-h-8 py-2 md:py-0 md:h-6 overflow-hidden ${className}`}
				style={{
					backgroundImage: 'linear-gradient(to right, #000000, #6B7280, #000000)'
				}}
			>
				<div className="relative z-10 h-full">
					<div className="flex flex-col md:flex-row items-center justify-between h-full container mx-auto gap-y-2 md:gap-y-0 px-4 md:px-6">
						<div className="flex items-center w-full md:w-auto">
							<span className="text-white text-base font-semibold">StudyStash partners with ProApptive to support Dutch institutions</span>
						</div>
						<div className="flex items-center gap-x-3 flex-wrap w-full md:w-auto justify-start md:justify-center px-0">
							<Link href={"/about/events-webinars/proapptive-partnership" as any} className="hidden sm:block text-white text-base hover:underline font-semibold">
								Read more →
							</Link>
							<div className="flex items-center">
								<Link href={"/nl-pilot" as any} className="text-white text-base hover:underline font-semibold">
									Our offer →
								</Link>
							</div>
						</div>
					</div>
				</div>
			</section>
		);
	}

	// Default and with-svg variants
	if (variant === 'default' || variant === 'with-svg') {
		return (
			<section className={`relative py-10 md:py-16 bg-linear-to-l from-ss-light-green via-ss-green to-ss-blue overflow-hidden ${className}`}>
				{/* Decorative SVG positioned on the right - only show if with-svg variant */}
				{variant === 'with-svg' && (
					<div className="absolute top-0 right-0 w-full h-full pointer-events-none z-0">
						<div className="relative w-full h-full flex items-start justify-end overflow-hidden">
							<div className="w-[450px] top-[-215px] left-[50px] sm:left-[50px] md:left-[50px] lg:left-[-100px] transition-all duration-500 ease-in-out relative">
								<img
									src="/two_lines.svg"
									alt="Illustration of the StudyStash teaching-learning loop"
									className="w-full h-full object-contain transition-all duration-500 ease-in-out"
									style={{
										transform: 'scale(0.6) translateX(0%)'
									}}
								/>
							</div>
						</div>
					</div>
				)}

				<div className="max-w-3xl ml-0 mr-auto px-0 lg:px-8 relative z-10 text-left">
					{children}
				</div>
			</section>
		);
	}

	// College variant
	if (variant === 'college') {
		return (
			<section className={`relative py-8 lg:py-12 bg-linear-to-l from-ss-light-green via-ss-green to-ss-blue overflow-hidden ${className}`}>
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
					<div className="flex justify-center">
						<div className="mb-4 flex min-h-fit flex-col items-center justify-center space-y-6 md:mt-0 md:flex-row md:space-x-4 md:space-y-0">
							<div className="flex flex-col md:space-y-4">
								<h1 className="text-xl font-bold lg:text-4xl sm:text-2xl text-white">{collegeTitle}</h1>
								<p className="pt-2 text-sm lg:text-xl sm:text-lg text-white opacity-90">{collegeSubtitle}</p>
								<div className="flex h-1/2 flex-row pt-3">
									<div className="flex flex-wrap gap-2">
										{subjectIcons?.map((subject, index) => (
											<Badge key={index} variant="secondary" className="w-max">
												{subject.icon} {subject.name}
											</Badge>
										))}
									</div>
								</div>
							</div>
							<div className="flex lg:pt-4 w-full flex-col items-start space-y-2 lg:items-center lg:justify-center md:w-8/12">
								{collegeImage && (
									<img
										src={collegeImage}
										width={1000}
										height={1000}
										alt={collegeImageAlt || "College Image"}
										className="rounded-3xl"
									/>
								)}
							</div>
						</div>
					</div>
				</div>
			</section>
		);
	}

	// Article variants
	if (variant === 'article' || variant === 'article-compact' || variant === 'article-compact-centered' || variant === 'article-compact-split') {
		const isCompactArticle = variant === 'article-compact';
		const isCompactCenteredArticle = variant === 'article-compact-centered';
		const isCompactSplitArticle = variant === 'article-compact-split';

		if (isCompactCenteredArticle) {
			return (
				<section
					className={`relative overflow-hidden border-b border-gray-200 bg-linear-to-br from-[#004C89] via-[#075B83] to-[#06324D] py-12 lg:py-16 ${className}`}
				>
					<div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
						<div className="mx-auto max-w-4xl text-center text-white">
							{eyebrowLabel && (
								<p className="mb-3 inline-flex items-center rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-gray-800">
									{eyebrowLabel}
								</p>
							)}
							{title && (
								<h1 className="mb-3 text-3xl! leading-[1.08] tracking-[-0.015em] text-white sm:text-4xl! lg:text-6xl!">
									{title}
								</h1>
							)}
							{description && (
								<p className="mx-auto max-w-3xl text-base leading-relaxed text-white/90 sm:text-lg">
									{description}
								</p>
							)}
							{children && <div className="mt-5">{children}</div>}
						</div>
					</div>
				</section>
			);
		}

		if (isCompactArticle) {
			const compactSectionBgClass = articleImage
				? "bg-linear-to-l from-ss-light-green via-ss-green to-ss-blue"
				: "bg-[#004C89]";

			return (
				<section className={`relative overflow-hidden border-b border-gray-200 ${compactSectionBgClass} py-12 lg:py-16 ${className}`}>
					{articleImage ? (
						<div className="absolute inset-0">
							<Image
								src={articleImage}
								alt={articleImageAlt || "Article image"}
								fill
								sizes="100vw"
								className="object-cover object-center"
								priority
							/>
						</div>
					) : null}
					{articleImage ? (
						<div className="pointer-events-none absolute inset-0 bg-linear-to-t from-ss-blue to-ss-blue/60" />
					) : null}

					<div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
						<div className="max-w-4xl text-white">
							<p className="mb-3 inline-flex items-center rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-gray-800">
								{eyebrowLabel || "Case study"}
							</p>
							{title && (
								<h1 className="mb-4 leading-[1.08] tracking-[-0.015em] text-white text-3xl! sm:text-4xl! lg:text-6xl!">
									{title}
								</h1>
							)}
							{description && (
								<p className="pb-6 text-base leading-relaxed text-white/90 sm:text-lg lg:pb-7">
									{description}
								</p>
							)}

							{(authorName || publishDate || readingTime) && (
								<div className="flex flex-wrap items-center gap-x-3 gap-y-2">
									{authorName && (
										<div className="inline-flex items-center gap-2.5">
											{authorImage ? (
												<div className="h-12 w-12 overflow-hidden rounded-full border border-white/70 ring-1 ring-white/35">
													<Image
														src={authorImage}
														alt={authorName}
														width={40}
														height={40}
														className="h-full w-full object-cover"
													/>
												</div>
											) : null}
											<div className="leading-tight">
												<p className="font-semibold text-white text-sm sm:text-base">{authorName}</p>
												{(publishDate || readingTime) && (
													<div className="inline-flex items-center gap-2 text-white/90">
														{publishDate && <span className="inline-flex items-center">{publishDate}</span>}
														{publishDate && readingTime && (
															<span className="inline-block h-1 w-1 rounded-full bg-white/65" aria-hidden="true"></span>
														)}
														{readingTime && <span className="inline-flex items-center text-white/80">{readingTime}</span>}
													</div>
												)}
											</div>
										</div>
									)}
								</div>
							)}

							{children && (
								<div className="mt-5">
									{children}
								</div>
							)}
						</div>
					</div>
				</section>
			);
		}

		if (isCompactSplitArticle) {
			const splitSectionBgClass = "bg-[#004C89]";

			return (
				<section className={`relative overflow-hidden border-b border-gray-200 ${splitSectionBgClass} py-12 lg:py-16 ${className}`}>
					<div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
						<div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)] lg:gap-12">
							<div className="text-white">
								<p className="mb-3 inline-flex items-center rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-gray-800">
									{eyebrowLabel || "Student success story"}
								</p>
								{title && (
									<h1 className="mb-4 leading-[1.08] tracking-[-0.015em] text-white text-3xl! sm:text-4xl! lg:text-6xl!">
										{title}
									</h1>
								)}
								{description && (
									<p className="pb-6 text-base leading-relaxed text-white/90 sm:text-lg lg:pb-7">
										{description}
									</p>
								)}

								{(authorName || publishDate || readingTime) && (
									<div className="flex flex-wrap items-center gap-x-3 gap-y-2">
										{authorName && (
											<div className="inline-flex items-center gap-2.5">
												{authorImage ? (
													<div className="h-12 w-12 overflow-hidden rounded-full border border-white/70 ring-1 ring-white/35">
														<Image
															src={authorImage}
															alt={authorName}
															width={40}
															height={40}
															className="h-full w-full object-cover"
														/>
													</div>
												) : null}
												<div className="leading-tight">
													<p className="font-semibold text-white text-sm sm:text-base">{authorName}</p>
													{(publishDate || readingTime) && (
														<div className="inline-flex items-center gap-2 text-white/90">
															{publishDate && <span className="inline-flex items-center">{publishDate}</span>}
															{publishDate && readingTime && (
																<span className="inline-block h-1 w-1 rounded-full bg-white/65" aria-hidden="true"></span>
															)}
															{readingTime && <span className="inline-flex items-center text-white/80">{readingTime}</span>}
														</div>
													)}
												</div>
											</div>
										)}
									</div>
								)}

								{children && (
									<div className="mt-5">
										{children}
									</div>
								)}
							</div>

							{articleImage ? (
								<div className="relative h-[300px] w-full sm:h-[360px] lg:h-[420px]">
									<div className="h-full w-full overflow-hidden rounded-2xl border border-white/20 shadow-2xl">
										<Image
											src={articleImage}
											alt={articleImageAlt || "Article image"}
											width={720}
											height={960}
											className="h-full w-full object-cover"
											priority
										/>
									</div>
								</div>
							) : null}
						</div>
					</div>
				</section>
			);
		}

		return (
			<section
				className={`relative overflow-hidden bg-linear-to-l from-ss-light-green via-ss-green to-ss-blue py-16 lg:py-32 ${className}`}
			>
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
						{/* Left side - Text content */}
						<div className="text-white">
							{title && (
								<h1 className="mb-6 leading-tight text-white text-4xl! sm:text-5xl! lg:text-6xl!">
									{title}
								</h1>
							)}
							{description && (
								<p className="pb-10 text-lg leading-relaxed text-white opacity-90 lg:text-xl">
									{description}
								</p>
							)}

							{/* Metadata */}
							{(authorName || publishDate || readingTime) && (
								<div className="flex flex-col space-y-2 md:flex-row md:items-center md:space-x-6 md:space-y-0">
									{/* Author section */}
									{authorImage && authorName && (
										<div className="flex items-center space-x-3">
											<div className="w-10 h-10 rounded-full overflow-hidden">
												<Image
													src={authorImage}
													alt={authorName}
													width={40}
													height={40}
													className="w-full h-full object-cover"
												/>
											</div>
											<span className="text-md text-white">{authorName}</span>
										</div>
									)}

									{/* Date and reading time section */}
									{(publishDate || readingTime) && (
										<div className="flex items-center">
											{/* Green divider - only show on desktop when author exists */}
											{authorName && (publishDate || readingTime) && (
												<div className="hidden h-6 w-0.5 bg-ss-light-green md:mr-6 md:block"></div>
											)}

											<div className="flex items-center space-x-6">
												{/* Date */}
												{publishDate && (
													<span className="text-md text-white opacity-90">{publishDate}</span>
												)}

												{/* Green divider */}
												{publishDate && readingTime && (
													<div className="h-6 w-0.5 bg-ss-light-green"></div>
												)}

												{/* Reading time */}
												{readingTime && (
													<span className="text-md text-white opacity-90">{readingTime}</span>
												)}
											</div>
										</div>
									)}
								</div>
							)}

							{/* Children content (for custom elements like video modals) */}
							{children && (
								<div className="mt-6">
									{children}
								</div>
							)}
						</div>

						{/* Right side - Image */}
						{articleImage && (
							<div className="relative h-[300px] w-full lg:h-[350px]">
								<div className="h-full w-full overflow-hidden rounded-2xl shadow-lg">
									<Image
										src={articleImage}
										alt={articleImageAlt || "Article image"}
										width={600}
										height={350}
										className={`h-full w-full bg-white ${articleImageFit === 'contain' ? 'object-contain' : 'object-cover'}`}
									/>
								</div>
							</div>
						)}
					</div>
				</div>
			</section>
		);
	}

	// Fallback to default
	return (
		<section className={`relative py-16 bg-linear-to-l from-ss-light-green via-ss-green to-ss-blue overflow-hidden ${className}`}>
			<div className="max-w-3xl ml-0 mr-auto px-4 sm:px-6 lg:px-8 relative z-10">
				{children}
			</div>
		</section>
	);
}
